import React, { useState, useEffect, Suspense, lazy } from "react";
import "./App.css";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "react-notifications/lib/notifications.css";
import { NotificationContainer } from "react-notifications";
import { useDispatch, useSelector } from "react-redux";
import { setLogout } from "./redux/slices/authSlice";
import { fetchHomepageData } from "./redux/thunks/pagesThunks";
import Header from "./components/Header";
import SideMenu from "./components/SideMenu";
import BottomMenu from "./components/BottomMenu";
import PageExtras from "./components/PageExtras";
import LoadingSpinner from "./components/LoadingSpinner";

const HomePage = lazy(() => import("./pages/HomePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const GatewayPage = lazy(() => import("./pages/GatewayPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const BouquetsPage = lazy(() => import("./pages/BouquetsPage"));
const AmountPage = lazy(() => import("./pages/AmountPage"));
const ViewItem = lazy(() => import("./pages/ViewItem"));
const WalletPage = lazy(() => import("./pages/WalletPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const FavoritePage = lazy(() => import("./pages/FavoritePage"));
const BuyItemPlayerId = lazy(() => import("./pages/BuyItemPlayerId"));
const BuyItem = lazy(() => import("./pages/BuyItem"));
const Deposit = lazy(() => import("./pages/Deposit"));
const DepositSub = lazy(() => import("./pages/DepositSub"));
const Noty = lazy(() => import("./pages/Noty"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const Agent = lazy(() => import("./pages/Agent"));
const Tickets = lazy(() => import("./pages/Tickets"));
const Ticket = lazy(() => import("./pages/Ticket"));
const ViewOrder = lazy(() => import("./pages/ViewOrder"));
const VirtualNumbers = lazy(() => import("./pages/VirtualNumbers"));
const MyActivations = lazy(() => import("./pages/MyActivations"));
const ViewActivation = lazy(() => import("./pages/ViewActivation"));
const OrderResultPage = lazy(() => import("./pages/OrderResultPage"));

function App() {
  const theme = useSelector((state) => state.ui.theme);
  const logged = useSelector((state) => state.auth.logged);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (logged != true && location.pathname !== "/login" && location.pathname !== "/register") {
      navigate("/login");
    }
  }, [logged, location.pathname]);

  useEffect(() => {
    if (logged === true) {
      dispatch(fetchHomepageData());
    }
  }, [logged, dispatch]);

  const handleToggle = () => {
    if (window.innerWidth <= 960) setMobileOpen((o) => !o);
    else setCollapsed((c) => !c);
  };
  const closeMobile = () => setMobileOpen(false);

  const logout = () => {
    dispatch(setLogout());
    navigate("/login");
  };

  const isDark = theme === "dark-theme" || theme === "dark";

  // Auth pages (/login, /register) are always shown as-is, regardless of the
  // persisted `logged` flag. This prevents a reload loop where a stale token
  // triggers a 401 -> interceptor redirect -> reload cycle.
  const isAuthPage = location.pathname === "/login" || location.pathname === "/register";

  if (isAuthPage || logged != true) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="*" element={<LoginPage />} />
        </Routes>
      </Suspense>
    );
  }

  const appClass = [
    "app",
    isDark && "is-dark",
    collapsed && "is-collapsed",
    mobileOpen && "is-mobile-open",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={appClass} id="app">
      <div
        className="scrim"
        id="scrim"
        style={{ display: mobileOpen ? "block" : "none" }}
        onClick={closeMobile}
      />
      <SideMenu onNavigate={closeMobile} />
      <div className="main">
        <Header onToggle={handleToggle} onLogout={logout} />
        <main className="content">
          <PageExtras />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" exact element={<HomePage />} />
              <Route path="bouquets/:id" exact element={<BouquetsPage />} />
              <Route path="amount/:id" exact element={<AmountPage />} />
              <Route path="Gateway/:id" exact element={<GatewayPage />} />
              <Route path="Profile" exact element={<ProfilePage />} />
              <Route path="wallet" exact element={<WalletPage />} />
              <Route path="view_order/:id" exact element={<ViewOrder />} />
              <Route path="orders" exact element={<OrdersPage />} />
              <Route path="search" exact element={<SearchPage />} />
              <Route path="view_item/:id" exact element={<ViewItem />} />
              <Route path="deposit" exact element={<Deposit />} />
              <Route path="deposit/:id" exact element={<DepositSub />} />
              <Route path="favorite" exact element={<FavoritePage />} />
              <Route path="buy_item/:id" exact element={<BuyItem />} />
              <Route path="noty" exact element={<Noty />} />
              <Route path="agent" exact element={<Agent />} />
              <Route path="tickets" exact element={<Tickets />} />
              <Route path="/ticket/:id" exact element={<Ticket />} />
              <Route path="virtual-numbers" exact element={<VirtualNumbers />} />
              <Route path="my-activations" exact element={<MyActivations />} />
              <Route path="activation/:id" exact element={<ViewActivation />} />
              <Route
                path="buy_item_player_id/:id"
                exact
                element={<BuyItemPlayerId />}
              />
              <Route path="order-result" exact element={<OrderResultPage />} />
            </Routes>
          </Suspense>
        </main>
      </div>
      <BottomMenu />
      <NotificationContainer />
    </div>
  );
}

export default App;
